import React, { useEffect, useState } from 'react';
import { getClientPerformance } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { ErrorBanner, Spinner } from './ClientDashboardPage';

const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(cents || 0) / 100);

export default function ClientSalesPage() {
  const { theme: T } = useClientTheme();
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analyticsInterval, setAnalyticsInterval] = useState('day');
  const [analyticsAudience, setAnalyticsAudience] = useState('external');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getClientPerformance({
      days: analyticsDays,
      interval: analyticsInterval,
      audience: analyticsAudience,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
    })
      .then((data) => { if (active) setPerformance(data?.performance || data?.sales || null); })
      .catch((err) => { if (active) setError(err?.response?.data?.error || 'Failed to load performance.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [analyticsDays, analyticsInterval, analyticsAudience]);

  if (loading) return <Spinner T={T} />;
  if (error) return <ErrorBanner T={T}>{error}</ErrorBanner>;

  const card = { borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18 };
  const maxProductUnits = Math.max(1, ...(performance?.byProduct || []).map((row) => Number(row.units || 0)));
  const analytics = performance?.analytics || {};

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 750, color: T.text }}>Performance</div>
        <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>
          Live reservations, revenue, product movement, and checkout signal.
        </div>
      </div>

      <section style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>Website Analytics</div>
            <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>
              {analytics.days || analyticsDays} day view · bucketed by {analytics.interval || analyticsInterval} · {analytics.audience || analyticsAudience} traffic
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Select value={analyticsDays} onChange={(value) => {
              const days = Number(value);
              setAnalyticsDays(days);
              setAnalyticsInterval((current) => getIntervalOptions(days).some((option) => option.value === current) ? current : getDefaultIntervalForDays(days));
            }} T={T}>
              <option value={1}>24 hours</option>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={365}>365 days</option>
            </Select>
            <Select value={analyticsInterval} onChange={setAnalyticsInterval} T={T}>
              {getIntervalOptions(analyticsDays).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
            <Select value={analyticsAudience} onChange={setAnalyticsAudience} T={T}>
              <option value="external">External visitors</option>
              <option value="internal">Internal visitors</option>
              <option value="all">All visitors</option>
            </Select>
          </div>
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Metric label="Revenue Collected" value={formatMoney(performance?.revenueCents || 0)} T={T} />
        <Metric label="Reservations" value={performance?.reservations || 0} T={T} />
        <Metric label="Units Reserved" value={performance?.units || 0} T={T} />
        <Metric label="Visitors" value={analytics.visitors || 0} T={T} />
      </div>

      <section style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
          <Metric label="Page Views" value={analytics.pageViews || 0} T={T} compact />
          <Metric label="Bounce Rate" value={`${Number(analytics?.bounce?.site?.bounceRate || 0).toFixed(1)}%`} T={T} compact />
          <Metric label="Hotspot Clicks" value={analytics.hotspotClicks || 0} T={T} compact />
          <Metric label="Overlay Opens" value={analytics.overlayOpens || 0} T={T} compact />
          <Metric label="Cart Adds" value={analytics.cartAdds || 0} T={T} compact />
          <Metric label="Checkout Starts" value={analytics.checkoutStarts || 0} T={T} compact />
          <Metric label="Paid Checkouts" value={Number(analytics.deposits || 0) + Number(analytics.fullPayments || 0)} T={T} compact />
          <Metric label="Abandoned Checkout" value={analytics?.checkout?.abandonedSessions || 0} T={T} compact />
          <Metric label="Leads" value={Number(analytics.emailCaptures || 0) + Number(analytics.inquiries || 0)} T={T} compact />
          <Metric label="Concept Signals" value={analytics.conceptSignals || 0} T={T} compact />
          <Metric label="Friction Events" value={analytics.frictionEvents || 0} T={T} compact />
          <Metric label="Internal Sessions" value={analytics?.trafficBreakdown?.internal?.sessions || 0} T={T} compact />
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
        <TimelineChart rows={analytics.timeline || []} T={T} />
        <FunnelChart rows={analytics.funnel || []} T={T} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
        <DataTable title="Abandoned Checkout" columns={['Product', 'Starts', 'Paid', 'Abandoned', 'Rate']} rows={(analytics?.checkout?.byProduct || []).map((row) => [
          row.productTitle || row.productId || 'Unknown',
          row.checkoutStarts || 0,
          row.successfulPayments || 0,
          row.abandonedSessions || 0,
          `${Number(row.abandonmentRate || 0).toFixed(1)}%`,
        ])} empty="No checkout abandonment data yet." T={T} />
        <DataTable title="Overlay Outcomes" columns={['Overlay', 'Purpose', 'Sessions', 'Outcome', 'Rate']} rows={(analytics?.bounce?.overlays || []).map((row) => [
          row.label || 'Unknown',
          purposeLabel(row.purpose),
          row.sessions || 0,
          row.successfulSessions || 0,
          `${Number(row.successRate || 0).toFixed(1)}%`,
        ])} empty="No overlay session data yet." T={T} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 16 }}>
        <Breakdown title="Event Categories" rows={(analytics.byCategory || []).map((row) => [categoryLabel(row.category), row.count])} empty="No event categories yet." T={T} />
        <Breakdown title="Top Storefront Paths" rows={(analytics.byPath || analytics.topPaths || []).map((row) => [row.path, row.count])} empty="No page traffic is visible yet." T={T} />
        <Breakdown title="All Events" rows={(analytics.byType || []).map((row) => [row.eventType, row.count])} empty="No events have been tracked yet." T={T} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 16 }}>
        <DataTable title="Page Bounce Rate" columns={['Page', 'Sessions', 'Bounced', 'Rate']} rows={(analytics?.bounce?.pages || []).map((row) => [
          row.path || 'Unknown',
          row.sessions || 0,
          row.bouncedSessions || 0,
          `${Number(row.bounceRate || 0).toFixed(1)}%`,
        ])} empty="No page session data yet." T={T} />
        <DataTable title="Visitor Locations" columns={['Location', 'Visitors', 'Sessions']} rows={(analytics.geography || []).map((row) => [
          [row.city, row.region, row.country].filter(Boolean).join(', ') || 'Unknown',
          row.visitors || 0,
          row.sessions || 0,
        ])} empty="No location data is visible yet." T={T} />
      </div>

      <RecentVisitors rows={analytics.recentVisitors || []} T={T} />

      <section style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>Product Performance</div>
        {(performance?.byProduct || []).length ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {performance.byProduct.map((row) => (
              <div key={row.productId || row.productTitle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, color: T.text, marginBottom: 6 }}>
                  <strong>{row.productTitle || 'Product'}</strong>
                  <span>{row.units} units · {formatMoney(row.revenueCents)}</span>
                </div>
                <div style={{ height: 7, borderRadius: 999, background: T.id === 'light' ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.max(4, (Number(row.units || 0) / maxProductUnits) * 100)}%`, height: '100%', background: T.gold }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: T.textMuted }}>No paid reservations are visible yet.</div>
        )}
      </section>

      <section style={card}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>Size Interest</div>
        {(performance?.bySize || []).length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {performance.bySize.map((row) => (
              <div key={row.size} style={{ borderRadius: 999, border: `1px solid ${T.border}`, padding: '7px 11px', color: T.text, fontSize: 12 }}>
                <strong>{row.size}</strong> <span style={{ color: T.textSub }}>{row.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: T.textMuted }}>No size data yet.</div>
        )}
      </section>

      <section style={{ ...card, marginTop: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>Colorway Interest</div>
        {(performance?.byColorway || []).length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {performance.byColorway.map((row) => (
              <div key={row.colorway} style={{ borderRadius: 999, border: `1px solid ${T.border}`, padding: '7px 11px', color: T.text, fontSize: 12 }}>
                <strong>{row.colorway}</strong> <span style={{ color: T.textSub }}>{row.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: T.textMuted }}>Colorway-level reservations are not being tracked yet.</div>
        )}
      </section>

    </div>
  );
}

function Metric({ label, value, T, compact = false }) {
  return (
    <div style={{ borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: compact ? 12 : 16 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: compact ? 20 : 24, fontWeight: 800, color: T.text }}>{value}</div>
    </div>
  );
}

function Breakdown({ title, rows, empty, T }) {
  const card = { borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18 };
  return (
    <section style={card}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>{title}</div>
      {rows.length ? (
        <div style={{ display: 'grid', gap: 9 }}>
          {rows.slice(0, 8).map(([label, count]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: T.text, fontSize: 13 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
              <strong>{count}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: T.textMuted }}>{empty}</div>
      )}
    </section>
  );
}

function Select({ value, onChange, children, T }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={{ borderRadius: 8, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.inputColor, padding: '8px 10px', fontSize: 12, outline: 'none' }}
    >
      {children}
    </select>
  );
}

function getDefaultIntervalForDays(days) {
  if (Number(days) <= 2) return 'hour';
  if (Number(days) > 120) return 'month';
  if (Number(days) > 45) return 'week';
  return 'day';
}

function getIntervalOptions(days) {
  if (Number(days) <= 2) return [{ value: 'hour', label: 'Hourly' }];
  const options = [{ value: 'day', label: 'Daily' }];
  if (Number(days) >= 30) options.push({ value: 'week', label: 'Weekly' });
  if (Number(days) >= 90) options.push({ value: 'month', label: 'Monthly' });
  return options;
}

function categoryLabel(category = '') {
  return String(category || '')
    .split('_')
    .map((part) => part ? part[0].toUpperCase() + part.slice(1) : '')
    .join(' ');
}

function purposeLabel(purpose = '') {
  if (purpose === 'product') return 'Product';
  if (purpose === 'concept') return 'Concept';
  if (purpose === 'informational') return 'Information';
  return 'Legacy';
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '-';
  }
}

function formatBucketLabel(bucket = '') {
  if (/^\d{4}-\d{2}-\d{2} \d{2}:00$/.test(bucket)) {
    const [, month, day, hour] = bucket.match(/^\d{4}-(\d{2})-(\d{2}) (\d{2}):00$/) || [];
    return `${Number(month)}/${Number(day)} ${Number(hour)}:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(bucket)) {
    const [, month, day] = bucket.match(/^\d{4}-(\d{2})-(\d{2})$/) || [];
    return `${Number(month)}/${Number(day)}`;
  }
  if (/^\d{4}-W\d{2}$/.test(bucket)) return bucket.replace('-', ' ');
  if (/^\d{4}-\d{2}$/.test(bucket)) {
    const [, month] = bucket.match(/^\d{4}-(\d{2})$/) || [];
    return `M${Number(month)}`;
  }
  return bucket;
}

function TimelineChart({ rows, T }) {
  const max = Math.max(1, ...rows.map((row) => Number(row?.events || 0)));
  const ticks = rows.length <= 8
    ? rows.map((_, index) => index)
    : Array.from({ length: 6 }, (_, index) => Math.round((index * (rows.length - 1)) / 5))
      .filter((index, position, list) => position === 0 || index !== list[position - 1]);
  return (
    <section style={{ borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>Traffic Over Time</div>
        <div style={{ fontSize: 11, color: T.textMuted }}>{rows.reduce((sum, row) => sum + Number(row.events || 0), 0)} events</div>
      </div>
      {rows.length ? (
        <>
          <div style={{ height: 206, display: 'flex', alignItems: 'flex-end', gap: 5, borderBottom: `1px solid ${T.border}`, paddingTop: 12 }}>
            {rows.map((row) => {
              const value = Number(row.events || 0);
              const height = value > 0 ? Math.max(4, Math.round((value / max) * 160)) : 2;
              return (
                <div key={row.bucket} title={`${row.bucket}: ${value}`} style={{ flex: 1, minWidth: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                  <div style={{ minHeight: 12, fontSize: 10, fontWeight: 800, color: value > 0 ? T.gold : T.textMuted }}>{value}</div>
                  <div style={{ width: '100%', maxWidth: 18, height, borderRadius: '5px 5px 0 0', background: value > 0 ? T.gold : T.border }} />
                </div>
              );
            })}
          </div>
          <div style={{ position: 'relative', height: 28, marginTop: 8, fontSize: 10, color: T.textMuted }}>
            {ticks.map((index) => {
              const left = rows.length > 1 ? (index / (rows.length - 1)) * 100 : 0;
              const isFirst = index === 0;
              const isLast = index === rows.length - 1;
              return (
                <div key={`${rows[index]?.bucket}-${index}`} style={{ position: 'absolute', left: `${left}%`, top: 0, transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)', whiteSpace: 'nowrap' }}>
                  {formatBucketLabel(rows[index]?.bucket)}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 13, color: T.textMuted }}>No analytics events yet.</div>
      )}
    </section>
  );
}

function FunnelChart({ rows, T }) {
  const max = Math.max(1, ...rows.map((row) => Number(row?.count || 0)));
  return (
    <section style={{ borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>Storefront Funnel</div>
      {rows.length ? rows.map((row) => {
        const value = Number(row.count || 0);
        const width = `${Math.max(4, Math.round((value / max) * 100))}%`;
        return (
          <div key={row.eventType} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4, fontSize: 12, color: T.text }}>
              <span>{row.label}</span>
              <strong>{value}</strong>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: T.id === 'light' ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.09)', overflow: 'hidden' }}>
              <div style={{ width, height: '100%', borderRadius: 999, background: T.gold }} />
            </div>
          </div>
        );
      }) : <div style={{ fontSize: 13, color: T.textMuted }}>No funnel data yet.</div>}
    </section>
  );
}

function DataTable({ title, columns, rows, empty, T }) {
  return (
    <section style={{ borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18, overflow: 'hidden' }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>{title}</div>
      {rows.length ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: T.text }}>
            <thead>
              <tr>{columns.map((column) => <th key={column} style={{ textAlign: 'left', padding: '8px 9px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{column}</th>)}</tr>
            </thead>
            <tbody>
              {rows.slice(0, 15).map((row, index) => (
                <tr key={`${title}-${index}`}>
                  {row.map((cell, cellIndex) => <td key={cellIndex} style={{ padding: '9px', borderBottom: `1px solid ${T.border}`, whiteSpace: cellIndex === 0 ? 'normal' : 'nowrap' }}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: T.textMuted }}>{empty}</div>
      )}
    </section>
  );
}

function RecentVisitors({ rows, T }) {
  return (
    <section style={{ borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>Recent Visitors</div>
        <div style={{ fontSize: 11, color: T.textMuted }}>{rows.length} sessions</div>
      </div>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
        IP is shown as a stable fingerprint so repeat test visits can be identified without storing raw IP addresses.
      </div>
      {rows.length ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: T.text }}>
            <thead>
              <tr>{['Last Seen', 'Visitor', 'Type', 'IP Fingerprint', 'Location', 'Device', 'Events', 'Paths'].map((column) => <th key={column} style={{ textAlign: 'left', padding: '8px 9px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{column}</th>)}</tr>
            </thead>
            <tbody>
              {rows.slice(0, 40).map((row) => {
                const location = [row.geo?.city, row.geo?.region, row.geo?.postalCode, row.geo?.country].filter(Boolean).join(', ') || 'Unknown';
                return (
                  <tr key={`${row.sessionId || row.visitorId}-${row.lastSeenAt}`}>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>{formatDate(row.lastSeenAt)}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${T.border}` }}>{String(row.visitorId || 'unknown').replace(/^visitor_/, '').slice(0, 12)}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${T.border}` }}>{row.isInternal ? 'Internal' : 'External'}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${T.border}`, fontFamily: 'monospace' }}>{String(row.ipFingerprint || 'not captured').slice(0, 12)}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${T.border}` }}>{location}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${T.border}` }}>{row.deviceClass || 'unknown'}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${T.border}` }}>{row.events || 0}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${T.border}`, minWidth: 220 }}>{(row.paths || []).join(', ') || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: T.textMuted }}>Recent visitor detail will appear for new tracked sessions.</div>
      )}
    </section>
  );
}
